import React from "react"
import { useDocumentsPerCaseStats } from "@/hooks/useDocumentsPerCaseStats"
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts"
import { FileText } from "lucide-react"

interface DocumentsPerCaseCardProps {
  days: number
  className?: string
}

export const DocumentsPerCaseCard: React.FC<DocumentsPerCaseCardProps> = ({
  days,
  className,
}) => {
  const { data, isLoading, error } = useDocumentsPerCaseStats(days)

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            Documents Per Case
          </CardTitle>
        </CardHeader>
        <CardContent className="h-80 flex items-center justify-center">
          <p className="text-muted-foreground">Loading statistics...</p>
        </CardContent>
      </Card>
    )
  }

  if (error || !data) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            Documents Per Case
          </CardTitle>
        </CardHeader>
        <CardContent className="h-80 flex items-center justify-center">
          <p className="text-destructive">Error loading statistics</p>
        </CardContent>
      </Card>
    )
  }

  const { average, median, min_count, max_count, distribution, total_cases } = data

  // If there are no cases, show a message
  if (total_cases === 0) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            Documents Per Case
          </CardTitle>
        </CardHeader>
        <CardContent className="h-80 flex items-center justify-center">
          <p className="text-muted-foreground">No cases in the selected time period</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-primary" />
          Documents Per Case
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <div className="flex flex-col">
            <span className="text-sm text-muted-foreground">Average</span>
            <span className="text-2xl font-semibold">{average.toFixed(1)}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-sm text-muted-foreground">Median</span>
            <span className="text-2xl font-semibold">{median.toFixed(0)}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-sm text-muted-foreground">Min</span>
            <span className="text-2xl font-semibold">{min_count}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-sm text-muted-foreground">Max</span>
            <span className="text-2xl font-semibold">{max_count}</span>
          </div>
        </div>

        <div className="h-48 mt-4">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={distribution} aria-label="Documents per case distribution chart">
              <XAxis 
                dataKey="bucket" 
                axisLine={false}
                tickLine={false}
              />
              <YAxis 
                allowDecimals={false}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip 
                formatter={(value: number) => [`${value} cases`, "Count"]}
                labelFormatter={(label) => `${label} documents`}
              />
              <Bar
                dataKey="count"
                name="Cases"
                fill="#4f46e5"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  )
} 